import React, { useState , useEffect, useRef, useCallback, useMemo } from 'react';
import Fuse from 'fuse.js';
import { API } from "aws-amplify";
import swal from '@sweetalert/with-react';

import Dropdown from '../../Dropdown';
import EditBlockSecondaryTeacher from './reusable-components/EditBlockSecondaryTeacher';
import loading_gif from "../../../images/ajax-loader.gif";

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashAlt, faSquare, faCheckSquare, faTimes, faSearch, faPlusCircle, faLock, faMinusSquare, faSortDown} from '@fortawesome/free-solid-svg-icons';
import { faSquare as farSquare } from '@fortawesome/free-regular-svg-icons';
import { useOutsideClick, cloneObj, createUniqueObjectID, isObject, getData, useDebouncedValue } from '../../../js/Helpers';

import '../../../css/elementary/edit_modals.css';

export default function EditBlock({ schoolInfo, days, blocks, setBlocks, courses, setCourses, students, teachers, classrooms, blockData, overlapCheck,  removeBlock, toggleEditBlock, sidePanelClosing, setSidePanelClosing, saveBlockOverride = null, renderBlockTypeExtras = null, onEditorStateChange = null, allowedBlockTypes = ['block', 'special', 'pullout', 'lunch'], showClassroomField = true, saveButtonLabel = 'Save' }) {
	///////////////////////////
	///// STATE VARIABLES /////
	///////////////////////////
	const [affectedBlocks, setAffectedBlocks] = useState([]);
	const [removedBlocks, setRemovedBlocks] = useState([]);
	const [addedBlocks, setAddedBlocks]= useState([]);

	const [blockDataImmutable, setBlockDataImmutable] = useState({});
	const [blockDataCopy, setBlockDataCopy] = useState(null);
	const [blockDataCopied, setBlockDataCopied] = useState(false);
	
	const [selectedStudents, setSelectedStudents] = useState([]);

	const [timeErrorMessage, setTimeErrorMessage] = useState(null);
	const [homeroomTeacherError, setHomeroomTeacherError] = useState(null);
	const [teachingTeacherError, setTeachingTeacherError] = useState(null);
	const [saveError, setSaveError] = useState(null);

	const [searchResults, setSearchResults] = useState({course_name:[], homeroom_teacher:[], teaching_teacher:[], suggested_students:[], students:[]});
	const [currentScreen, setCurrentScreen] = useState(1);
	const [showStudentSearch, setShowStudentSearch] = useState(false);
	const [changesMade, setChangesMade] = useState(false);

	const [teachingTeacherName, setTeachingTeacherName] = useState('');
	const [homeroomTeacherName, setHomeroomTeacherName] = useState('');

	const [classroomOptions, setClassroomOptions] = useState([{'value': null, display:'--'}]);
	const [isSaving, setIsSaving] = useState(false);

	const courseFuse = useMemo(() => new Fuse(courses, {
		keys: ['name', 'course_code'],
		threshold: .1
	}), [courses]);

	const teacherFuse = useMemo(() => new Fuse(teachers, {
		keys: ['first_name', 'name'],
		threshold: .1
	}), [teachers]);

	const classroomFuse = useMemo(() => new Fuse(classrooms, {
		keys: ['name'],
		threshold: .1
	}), [classrooms]);
	const allowedBlockTypeSet = useMemo(() => new Set(allowedBlockTypes), [allowedBlockTypes]);

	const debouncedStartTime = useDebouncedValue(blockDataCopy ? blockDataCopy.start_time : '', 120);
	const debouncedEndTime = useDebouncedValue(blockDataCopy ? blockDataCopy.end_time : '', 120);
	const blockDataCopyBlockId = blockDataCopy ? blockDataCopy.block_id : null;

	///////////////////
	///// USE REF /////
	///////////////////
	const side_panel_ref = useRef();
	const course_search_ref = useRef();
	const homeroom_teachers_ref = useRef();
	const teaching_teachers_ref = useRef();

	useOutsideClick(side_panel_ref, () => {
		if(!blockData) return;
		handleExitWithoutSaving(false);
	}, ['elem-suggestion-box-parent', 'mb-add-new-block-options-container', 'swal-button--cancel', 'swal-button--confirm', 'dropdown-option', 'cancel-student-search', 'elem-edit-block-student', 'select-all-row', 'select-all-container', 'select-all-actions-container', 'elem-edit-block-remove-secondary-teacher']);

	useOutsideClick(course_search_ref, () => {
		setSearchResults({...searchResults, ...{course_name:[]}});
	}, []);

	useOutsideClick(teaching_teachers_ref, () => {
		setSearchResults({...searchResults, ...{teaching_teacher:[]}});
	}, []);

	useOutsideClick(homeroom_teachers_ref, () => {
		setSearchResults({...searchResults, ...{homeroom_teacher:[]}});
	}, []);
	
	/////////////////////
	///// FUNCTIONS /////
	/////////////////////
	const closeSidePanel = () => {
		toggleEditBlock(null);

		if(blockData) setSidePanelClosing(true);
		setTimeErrorMessage(null);
		setHomeroomTeacherError(null);
		setTeachingTeacherError(null);
		setAffectedBlocks([]);
		setRemovedBlocks([]);
		setAddedBlocks([]);
		setSaveError(null);
		setChangesMade(false);
		setBlockDataCopied(false);
	}

	const fuzzySearch = (e, dataType, dataKey = dataType) => {
		const search_value = e.target.value;
		
		if(dataType === 'courses')
		{
			const results = courseFuse.search(search_value);
			const course_results = results.map(result => result.item).slice(0, 20);
			
			searchResults[dataKey] = (course_results.length === 0 && search_value.length === 0) ? courses : course_results;
		}
		else if(dataType === 'teachers')
		{
			const results = teacherFuse.search(search_value);
			const teacher_results = results.map(result => result.item).slice(0, 6);
			
			searchResults[dataKey] = (teacher_results.length === 0 && search_value.length === 0) ? teachers : teacher_results;
		}
		else if(dataType === 'classrooms')
		{
			const results = classroomFuse.search(search_value);
			const classroom_results = results.map(result => result.item).slice(0, 6);
			
			searchResults[dataKey] = (classroom_results.length === 0 && search_value.length === 0) ? classrooms : classroom_results;
		}
		else if(dataType === 'students')
		{
			// Search all students
			const fuse_students = new Fuse(students, {
				keys: ['first_name', 'last_name'],
				threshold: .1
			});
			const results = fuse_students.search(search_value);
			const student_results = results.map(result => result.item).slice(0, 30);
			
			searchResults['students'] = (student_results.length === 0 && search_value.length === 0) ? students : student_results;

			// Search through homeroom students
			const suggested_students = (blockDataCopy.homeroom_teacher_id) ? students.filter(student => {
				const student_homeroom_teacher_id = student.homeroom_teacher_id;
				if(student_homeroom_teacher_id && (student_homeroom_teacher_id === blockDataCopy.homeroom_teacher_id)) return true;
				return false;
			}) : [];
			const fuse_suggested_students = new Fuse(suggested_students, {
				keys: ['first_name', 'last_name'],
				threshold: .1
			});
			const suggested_results = fuse_suggested_students.search(search_value);
			const suggested_student_results = suggested_results.map(result => result.item).slice(0, 30);

			searchResults['suggested_students'] = (suggested_student_results.length === 0 && search_value.length === 0) ? suggested_students : suggested_student_results;
		}

		setSearchResults({...searchResults});
	}

	const validateTime = (start_time, end_time) => {
		const day_start_time = 700;
		const day_end_time = 1600;

		if(!start_time || !end_time) return {status:'error', error:'Missing start or end time'};

		const start_time_array = start_time.split(':');
		const end_time_array = end_time.split(':');

		const start_time_number = parseInt(start_time_array[0] + start_time_array[1]);
		const end_time_number = parseInt(end_time_array[0] + end_time_array[1]);

		if(start_time_number < day_start_time) return {status:'error', error:'Blocks cannot start before the start of the day'};
		if(start_time_number > day_end_time) return {status:'error', error:'Blocks cannot start after the end of the day'};
		if(end_time_number < day_start_time) return {status:'error', error:'Blocks cannot end before the start of the day'};
		if(end_time_number > day_end_time) return {status:'error', error:'Blocks cannot end after the end of the day'};
		if(start_time_number > end_time_number) return {status:'error', error:'Your block has a start time that is after the end time'};

		return {status:'valid'};
	}

	const createTeacherFullName = useCallback((teacher_id) => {
		const teacher_index = teachers.findIndex(teacher => teacher.teacher_id === teacher_id);
		const teacher_info = (teacher_index !== -1) ? teachers[teacher_index] : null;
		const teacher_first_name = (teacher_info) ? teacher_info.first_name : null;
		const teacher_last_name = (teacher_info) ? teacher_info.name : null;
		const teacher_full_name = (teacher_info) ? `${teacher_last_name}, ${teacher_first_name}` : '';

		return teacher_full_name;
	},[teachers])

	const getHomeroomStudentsForTeacher = useCallback((teacher_id) => {
		if(!teacher_id) return [];
		return students.reduce((results, student) => {
			if(`${student.homeroom_teacher_id || ''}` === `${teacher_id}`) results.push(student.student_id);
			return results;
		}, []);
	}, [students]);

	const getTeacherDefaultClassroom = useCallback((teacher_id) => {
		const teacher_info = teachers.find((teacher) => `${teacher.teacher_id}` === `${teacher_id}`);
		return teacher_info ? (teacher_info.homeroom_classroom_id || null) : null;
	}, [teachers]);

	const syncBlockDayCollections = useCallback((block_entries = [], block_info = null) => {
		const normalized_entries = [...block_entries]
			.filter((entry) => entry && `${entry.day_number || ''}`.trim() !== '')
			.sort((entry_a, entry_b) => parseInt(entry_a.day_number, 10) - parseInt(entry_b.day_number, 10));

		const seen_days = new Set();
		const normalized_block_days = [];
		normalized_entries.forEach((entry) => {
			const normalized_day_number = `${entry.day_number}`;
			if(seen_days.has(normalized_day_number)) return;
			seen_days.add(normalized_day_number);
			normalized_block_days.push({
				block_day_id: entry.block_day_id || null,
				day_number: normalized_day_number,
			});
		});

		const normalized_days = normalized_block_days.map((block_day) => `${block_day.day_number}`);
		const cloned_block_days = normalized_block_days.map((block_day) => ({...block_day}));

		normalized_entries.forEach((entry) => {
			entry.days = [...normalized_days];
			entry.block_days = cloned_block_days.map((block_day) => ({...block_day}));
		});

		if(block_info)
		{
			block_info.days = [...normalized_days];
			block_info.block_days = cloned_block_days.map((block_day) => ({...block_day}));
		}

		return {
			days: normalized_days,
			block_days: cloned_block_days,
		};
	}, []);

	const handleExtraTeacherSearch = (e, teacher_type) => {
		fuzzySearch(e, 'teachers', teacher_type);

		if(teacher_type === 'teaching_teacher') 
		{
			setTeachingTeacherName(e.target.value);
			setBlockDataCopy({...blockDataCopy, ...{teacher_id:null}});
		}
		else if(teacher_type === 'homeroom_teacher') 
		{
			setHomeroomTeacherName(e.target.value);
			setBlockDataCopy({...blockDataCopy, ...{homeroom_teacher_id:null}});
		}
		
		setTeachingTeacherError('No valid teacher selected yet!');
		setChangesMade(true);
	} 

	const updateState = (e, key, options = {}) => {
		const { callback = ()=>{}, updateBoardPreview = true } = options;
		setChangesMade(true);
		setTimeErrorMessage(null);

		const value = (isObject(e) && 'target' in e) ? e.target.value : e;
		const nextBlockDataCopy = {...blockDataCopy, [key]: value};

		// If updated data is start or end time, make sure they are within the range
		// If not, return error
		if(key === 'end_time' || key === 'start_time')
		{
			const validated_time = validateTime(nextBlockDataCopy.start_time, nextBlockDataCopy.end_time);
			if(validated_time.status === 'error') setTimeErrorMessage(validated_time.error);
		}

		// Change the copied block data values
		setBlockDataCopy(nextBlockDataCopy);

		// Update original block so you can see the changes made in real time
		if(updateBoardPreview && nextBlockDataCopy.block_id)
		{
			let blocks_updated = false;
			affectedBlocks.forEach(affected_block => {
				const affected_block_day_id = affected_block.block_day_id;
				const block_index = blocks.findIndex(block => `${block.block_day_id || ''}` === `${affected_block_day_id || ''}`);
				if(block_index !== -1 && blocks[block_index][key] !== value)
				{
					blocks[block_index][key] = value;
					blocks_updated = true;
				}
			});
				
			if(blocks_updated) setBlocks([...blocks]);
		}
	

		callback();
	}

	const changeBlockType = (next_type) => {
		if(!blockDataCopy || blockData.locked === '1') return;

		const current_default_teacher_id = blockDataCopy.homeroom_teacher_id || blockDataImmutable.homeroom_teacher_id || blockDataImmutable.teacher_id || blockDataCopy.teacher_id || null;
		const next_block_copy = {...blockDataCopy};

		next_block_copy.is_lunch = next_type === 'lunch' ? '1' : '0';
		next_block_copy.is_pullout = next_type === 'pullout' ? '1' : '0';
		next_block_copy.is_special = next_type === 'special' ? '1' : '0';
		next_block_copy.course_id = null;
		next_block_copy.secondary_teachers = [];
		next_block_copy.block_name = next_type === 'lunch' ? 'Lunch' : 'Untitled';

		if(next_type === 'special')
		{
			next_block_copy.homeroom_teacher_id = current_default_teacher_id;
			next_block_copy.teacher_id = null;
			next_block_copy.classroom_id = getTeacherDefaultClassroom(current_default_teacher_id);
			next_block_copy.student_list = getHomeroomStudentsForTeacher(current_default_teacher_id);
		}
		else if(next_type === 'pullout')
		{
			next_block_copy.homeroom_teacher_id = current_default_teacher_id;
			next_block_copy.teacher_id = null;
			next_block_copy.classroom_id = null;
			next_block_copy.student_list = getHomeroomStudentsForTeacher(current_default_teacher_id);
		}
		else
		{
			next_block_copy.homeroom_teacher_id = current_default_teacher_id;
			next_block_copy.teacher_id = current_default_teacher_id;
			next_block_copy.classroom_id = getTeacherDefaultClassroom(current_default_teacher_id);
			next_block_copy.student_list = getHomeroomStudentsForTeacher(current_default_teacher_id);
		}

		next_block_copy.locked_students = (next_block_copy.locked_students || []).filter((student_id) => next_block_copy.student_list.includes(student_id));

		affectedBlocks.forEach((affected_block) => {
			const affected_block_day_id = `${affected_block.block_day_id || ''}`;
			const block_index = blocks.findIndex((block) => `${block.block_day_id || ''}` === affected_block_day_id);
			if(block_index !== -1)
			{
				blocks[block_index] = {
					...blocks[block_index],
					is_lunch: next_block_copy.is_lunch,
					is_pullout: next_block_copy.is_pullout,
					is_special: next_block_copy.is_special,
					course_id: next_block_copy.course_id,
					classroom_id: next_block_copy.classroom_id,
					homeroom_teacher_id: next_block_copy.homeroom_teacher_id,
					teacher_id: next_block_copy.teacher_id,
					student_list: [...next_block_copy.student_list],
					secondary_teachers: [...next_block_copy.secondary_teachers]
				};
			}
		});

		setTeachingTeacherName(createTeacherFullName(next_block_copy.teacher_id));
		setHomeroomTeacherName(createTeacherFullName(next_block_copy.homeroom_teacher_id));
		setSearchResults({...searchResults, course_name:[], homeroom_teacher:[], teaching_teacher:[]});
		setTeachingTeacherError(null);
		setHomeroomTeacherError(null);
		setSaveError(null);
		setChangesMade(true);
		setBlocks([...blocks]);
		setBlockDataCopy(next_block_copy);
	}

	const updateBlockName = (course_id, block_name) => {
		setChangesMade(true);
		setTimeErrorMessage(null);
		setSaveError(null);

		const nextBlockDataCopy = {
			...blockDataCopy,
			course_id: course_id,
			block_name: block_name
		};

		setBlockDataCopy(nextBlockDataCopy);

		if(nextBlockDataCopy.block_id)
		{
			let blocks_updated = false;
			affectedBlocks.forEach((affected_block) => {
				const affected_block_day_id = `${affected_block.block_day_id || ''}`;
				const block_index = blocks.findIndex((block) => `${block.block_day_id || ''}` === affected_block_day_id);
				if(block_index !== -1)
				{
					if(blocks[block_index].course_id !== course_id)
					{
						blocks[block_index].course_id = course_id;
						blocks_updated = true;
					}

					if(blocks[block_index].block_name !== block_name)
					{
						blocks[block_index].block_name = block_name;
						blocks_updated = true;
					}
				}
			});

			if(blocks_updated) setBlocks([...blocks]);
		}

		// Close suggestion box
		searchResults.course_name = [];
		setSearchResults({...searchResults});
	}

	const updatePullOutTeacher = (teacher_id) => {
		const teacher_name = createTeacherFullName(teacher_id);

		blockDataCopy.teacher_id = teacher_id;
		
		setTeachingTeacherError(null);
		setTeachingTeacherName(teacher_name);
		setBlockDataCopy({...blockDataCopy});

		// Close suggestion box
		searchResults.teaching_teacher = [];
		setSearchResults({...searchResults});

		setChangesMade(true);
	}

	const updateExtraTeacher = (teacher_id, teacher_type) => {
		const teacher_name = createTeacherFullName(teacher_id);

		if(teacher_type === 'homeroom_teacher')
		{
			// Update student list to be for this homeroom teacher
			const student_list = students.reduce((results, student) => {
				if(student.homeroom_teacher_id && student.homeroom_teacher_id === teacher_id) results.push(student.student_id);
				return results;
			}, []);

			blockDataCopy.homeroom_teacher_id = teacher_id;
			blockDataCopy.student_list = student_list;
			
			setHomeroomTeacherError(null);
			setHomeroomTeacherName(teacher_name);
		}
		else if(teacher_type === 'teaching_teacher')
		{
			blockDataCopy.teacher_id = teacher_id;
			
			setTeachingTeacherError(null);
			setTeachingTeacherName(teacher_name);
		}

		setBlockDataCopy({...blockDataCopy});

		// Close suggestion box
		searchResults[teacher_type] = [];
		setSearchResults({...searchResults});
		setChangesMade(true);
	}

	const addSecondaryTeacher = (passedData) => {
		const secondary_teacher_id = passedData.secondary_teacher_id;

		if(!secondary_teacher_id || secondary_teacher_id === '')
		{
			blockDataCopy.secondary_teachers.push(secondary_teacher_id);
		}
		else
		{
			const secondary_teacher_index = blockDataCopy.secondary_teachers.findIndex(st_id => st_id === '');
			blockDataCopy.secondary_teachers[secondary_teacher_index] = secondary_teacher_id;
		}
		
		setBlockDataCopy({...blockDataCopy});
		setChangesMade(true);
	}

	const changeSecondaryTeacher = (passedData) => {
		const old_secondary_teacher_id = passedData.old_secondary_teacher_id;
		const new_secondary_teacher_id = passedData.new_secondary_teacher_id;
		
		// Update the state
		const secondary_teacher_index = blockDataCopy.secondary_teachers.findIndex(st_id => st_id === old_secondary_teacher_id);
		blockDataCopy.secondary_teachers[secondary_teacher_index] = new_secondary_teacher_id;
		
		setBlockDataCopy({...blockDataCopy});
		setChangesMade(true);
	}

	const removeSecondaryTeacher = (passedData) => {
		const secondary_teacher_id = passedData.secondary_teacher_id;

		// Update the state
		const secondary_teacher_index = blockDataCopy.secondary_teachers.findIndex(st_id => st_id === secondary_teacher_id);
		if(secondary_teacher_index !== -1) blockDataCopy.secondary_teachers.splice(secondary_teacher_index, 1);
		
		setBlockDataCopy({...blockDataCopy});
		setChangesMade(true);
	}

	const updateDays = (update_type, day) => {
		if(update_type === 'remove')
		{
			const block_index = blocks.findIndex(block => affectedBlocks.findIndex(affected_block => `${affected_block.block_day_id || ''}` === `${block.block_day_id || ''}`) !== -1 && `${block.day_number}` === `${day}`);
			const block_id_to_remove = (block_index !== -1) ? blocks[block_index].block_id : null;
			const block_info_removed = (block_index !== -1) ? cloneObj(blocks[block_index]) : null;

			if(block_id_to_remove)
			{
				const affected_block_index = affectedBlocks.findIndex(affected_block => `${affected_block.block_day_id || ''}` === `${block_info_removed.block_day_id || ''}`);
				const added_block_index = addedBlocks.findIndex(added_block => `${added_block.block_day_id || ''}` === `${block_info_removed.block_day_id || ''}`);

				// remove from blocks
				blocks.splice(block_index, 1);

				// remove from affected blocks
				affectedBlocks.splice(affected_block_index, 1);

				if(added_block_index !== -1)
				{
					// remove from added blocks
					addedBlocks.splice(added_block_index, 1);
				}
				else
				{
					// add to removed blocks
					removedBlocks.push(block_info_removed);
				}

				syncBlockDayCollections(affectedBlocks, blockDataCopy);

				setBlocks([...blocks]);
				setAffectedBlocks([...affectedBlocks]);
				setRemovedBlocks([...removedBlocks]);
				setAddedBlocks([...addedBlocks]);
			}
		}
		else if(update_type === 'add')
		{
			/// Get all the current block data
			const course_id = blockDataCopy.course_id;
			const classroom_id = blockDataCopy.classroom_id;
			const end_time = blockDataCopy.end_time;
			const homeroom_teacher_id = blockDataCopy.homeroom_teacher_id;
			const is_lunch = blockDataCopy.is_lunch;
			const is_pullout = blockDataCopy.is_pullout;
			const is_special = blockDataCopy.is_special;
			const start_time = blockDataCopy.start_time;
			const block_name = blockDataCopy.block_name;
			const teacher_id =blockDataCopy.teacher_id;
			const student_list = [...blockDataCopy.student_list];
			const locked_students = [...(blockDataCopy.locked_students || [])];
			const secondary_teachers = [...blockDataCopy.secondary_teachers];

			// Check to see if there is a removed block with same day
			const removed_block_index = removedBlocks.findIndex(removed_block => `${removed_block.day_number}` === `${day}`);

			if(removed_block_index !== -1)
			{
				const removed_block_info = removedBlocks[removed_block_index];

				removed_block_info.course_id = course_id;
				removed_block_info.classroom_id = classroom_id;
				removed_block_info.end_time = end_time;
				removed_block_info.homeroom_teacher_id = homeroom_teacher_id;
				removed_block_info.is_lunch = is_lunch;
				removed_block_info.is_pullout = is_pullout;
				removed_block_info.is_special = is_special;
				removed_block_info.start_time = start_time;
				removed_block_info.teacher_id = teacher_id;
				removed_block_info.student_list = student_list;
				removed_block_info.locked_students = locked_students;
				removed_block_info.secondary_teachers = secondary_teachers;
				removed_block_info.block_name = block_name;

				// Add block back into the array
				blocks.push(removed_block_info);
				affectedBlocks.push(removed_block_info);

				// Take this block off the removed list
				removedBlocks.splice(removed_block_index, 1);

				syncBlockDayCollections(affectedBlocks, blockDataCopy);
				
				setBlocks([...blocks]);
				setAffectedBlocks([...affectedBlocks]);
				setRemovedBlocks([...removedBlocks]);
			}
			else
			{
				const block_id = blockDataCopy.block_id || createUniqueObjectID(5);
				const new_block = {
					block_id:block_id,
					block_day_id:createUniqueObjectID(8),
					course_id:course_id,
					classroom_id:classroom_id,
					day_number:`${day}`,
					end_time:end_time,
					homeroom_teacher_id:homeroom_teacher_id,
					is_lunch:is_lunch,
					is_pullout:is_pullout,
					is_special:is_special,
					start_time:start_time,
					teacher_id:teacher_id,
					student_list:student_list,
					locked_students:locked_students,
					secondary_teachers:secondary_teachers,
					block_name:block_name,
					new:blockDataCopy.new === true,
					locked:'0'
				};

				blocks.push(new_block);
				affectedBlocks.push(new_block);
				addedBlocks.push(new_block);

				syncBlockDayCollections(affectedBlocks, blockDataCopy);

				setBlocks([...blocks]);
				setAffectedBlocks([...affectedBlocks]);
				setAddedBlocks([...addedBlocks]);
			}
		}

		setChangesMade(true);
	}

	const addStudent = (student_id) => {
		const student_index = blockDataCopy.student_list.findIndex(temp_student_id => temp_student_id === student_id);

		if(student_index === -1)
		{
			blockDataCopy.student_list.push(student_id);
			setBlockDataCopy({...blockDataCopy});

			setChangesMade(true);
		}
	}

	const removeStudent = (student_id) => {
		const student_index = blockDataCopy.student_list.findIndex(temp_student_id => temp_student_id === student_id);

		if(student_index !== -1)
		{
			blockDataCopy.student_list.splice(student_index, 1);
			setBlockDataCopy({...blockDataCopy});

			setChangesMade(true);
		}
	}

	const toggleSelectStudent = (student_id) => {
		const student_selected_index = selectedStudents.findIndex(student => student === student_id);

		if(student_selected_index !== -1)
		{
			selectedStudents.splice(student_selected_index, 1);
		}
		else
		{
			selectedStudents.push(student_id);
		}

		setSelectedStudents([...selectedStudents]);
	}

	const toggleSelectAll = () => {
		const student_list = blockDataCopy.student_list.filter(student_id => !blockDataCopy.locked_students.includes(student_id));
		const any_students_selected = selectedStudents.filter(student_id => student_list.includes(student_id)).length > 0;
		
		let new_selected_students = [];

		if(any_students_selected)
		{
			new_selected_students = selectedStudents.filter(student_id => !student_list.includes(student_id));
		}
		else
		{
			new_selected_students = [...selectedStudents, ...student_list];
		}

		setSelectedStudents([...new_selected_students]);
	}

	const toggleLockStudents = (lock_type, student_id = null) => {
		if(lock_type === 'lock')
		{
			if(student_id)
			{
				const lock_index = blockDataCopy.locked_students.findIndex(locked_student_id => locked_student_id === student_id);
				if(lock_index === -1) blockDataCopy.locked_students.push(student_id);
			}
			else
			{
				selectedStudents.forEach(student_id => {
					blockDataCopy.locked_students.push(student_id);
				});
	
				setSelectedStudents([]);
			}
		}
		else if(lock_type === 'unlock')
		{
			const lock_index = blockDataCopy.locked_students.findIndex(locked_student_id => locked_student_id === student_id);
			if(lock_index !== -1) blockDataCopy.locked_students.splice(lock_index, 1);
		}
		
		setBlockDataCopy({...blockDataCopy});
		setChangesMade(true);
	}

	const bulkRemoveStudents = async () => {
		const options =  {
			title: "Are you sure?",
			icon: "warning",
			dangerMode: true,
			buttons: {
				cancel: {
					text: "Cancel",
					value: false,
					visible: true,
					className: 'gray-btn'
				},
				confirm: {
					text: "Yes",
					value: true,
					visible: true,
					className: 'red-btn'
				},
			},
			content: (
				<div>
					<p>Do you really want to remove the selected students from this block?</p>
				</div>
			)
		}

		if(await swal(options))
		{
			const selected_students_from_section = selectedStudents.filter(student_id => blockDataCopy.student_list.includes(student_id));
			const selected_students_not_from_section = selectedStudents.filter(student_id => !blockDataCopy.student_list.includes(student_id));
			
			selected_students_from_section.forEach(student_id => {
				removeStudent(student_id);
			});

			setSelectedStudents([...selected_students_not_from_section]);
			setChangesMade(true);
		}
	}

	const saveBlock = async () => {
		setIsSaving(true);

		if(affectedBlocks.length === 0)
		{
			if(saveBlockOverride)
			{
				const overrideResult = await saveBlockOverride({
					blockData,
					blockDataCopy,
					blockDataImmutable,
					affectedBlocks,
					removedBlocks,
					addedBlocks,
					blocks,
					courseId: blockDataCopy ? blockDataCopy.course_id : null,
					courses,
					students,
					teachers,
					classrooms
				});

				const overrideHandled = overrideResult === true || (overrideResult && overrideResult.handled);
				if(overrideHandled)
				{
					setIsSaving(false);
					if(overrideResult !== true && overrideResult.close === false) return;
					closeSidePanel();
					return;
				}
			}

			if(removedBlocks.length > 0)
			{
				await removeBlock(blockDataCopy.block_id, null, { skipConfirm:true });
			}
			else
			{
				addedBlocks.forEach((added_block) => {
					const added_block_day_id = added_block.block_day_id;
					const block_index = blocks.findIndex((block) => `${block.block_day_id || ''}` === `${added_block_day_id || ''}`);
					if(block_index !== -1) blocks.splice(block_index, 1);
				});

				setBlocks([...blocks]);
				setIsSaving(false);
				closeSidePanel();
			}

			return false;
		}

		//////////////////////////
		// Validate the block data
		//////////////////////////
		const course_input = document.getElementById('elem-edit-block-name');
		const start_time_input = document.getElementById('elem-edit-block-start-time');
		const end_time_input = document.getElementById('elem-edit-block-end-time');
		
		if(!course_input.value || course_input.value === 'Untitled')
		{
			setSaveError("Missing a block name!");
			setIsSaving(false);
			return false;
		}
		else if(!start_time_input.value || start_time_input.value === '00:00:00' || !end_time_input.value || end_time_input.value === '00:00:00')
		{
			setSaveError("Missing a start/end time!");
			setIsSaving(false);
			return false;
		}
		else if(blockDataCopy.is_pullout === '1')
		{
			const pullout_input = document.getElementById('elem-edit-block-pullout-teacher');

			if(!pullout_input.value)
			{
				setSaveError("Missing a pull out teacher!");
				setIsSaving(false);
				return false;
			}
		}
		else if(!blockDataCopy.teacher_id)
		{
			setSaveError("Missing a specials teacher!");
			setIsSaving(false);
			return false;
		}
		else if(!blockDataCopy.homeroom_teacher_id)
		{
			setSaveError("Missing a homeroom teacher!");
			setIsSaving(false);
			return false;
		}

		// If course is new, add/save the new course, then use for this block
		const block_name = blockDataCopy.block_name;
		let course_id = blockDataCopy.course_id;
		
		if(!blockDataCopy.course_id)
		{
			const is_special = blockDataCopy.is_special;
			const is_lunch = blockDataCopy.is_lunch;
			const is_pullout = blockDataCopy.is_pullout;
			const course_data = {name:block_name, is_special:is_special, is_lunch:is_lunch, is_pullout:is_pullout};

			const data = {school_id:schoolInfo.school_id, schedule_version_id:schoolInfo.current_schedule_version_id, course_data:course_data};
			const apiName = process.env.REACT_APP_ELEM_ENDPOINT_NAME;
			const url = '/elementary/save-course';
			const myInit = { // OPTIONAL
				response: true,
				body: JSON.stringify(data),
			};
		
			try {
				const result = await API.post(apiName, url, myInit);
				const response_data = result.data;

				course_id = response_data.new_course_id;

				courses.push({course_id:course_id, name:block_name, course_code:null, course_homerooms:[], data_origin:'2', classroom_restirctions:[], day_period_restrictions:[], is_lunch:is_lunch, is_pullout:is_pullout, is_special:is_special });
				setCourses([...courses]);
			} catch(e)
			{
				console.log(e.response);
				setIsSaving(false);
			}
		}

		if(saveBlockOverride)
		{
			const overrideResult = await saveBlockOverride({
				blockData,
				blockDataCopy,
				blockDataImmutable,
				affectedBlocks,
				removedBlocks,
				addedBlocks,
				blocks,
				courseId: course_id,
				courses,
				students,
				teachers,
				classrooms
			});

			const overrideHandled = overrideResult === true || (overrideResult && overrideResult.handled);
			if(overrideHandled)
			{
				setIsSaving(false);
				if(overrideResult !== true && overrideResult.close === false) return;
				closeSidePanel();
				return;
			}
		}

		const classroom_id = blockDataCopy.classroom_id;
		const end_time = blockDataCopy.end_time;
		const homeroom_teacher_id = blockDataCopy.homeroom_teacher_id;
		const is_lunch = blockDataCopy.is_lunch;
		const is_pullout = blockDataCopy.is_pullout;
		const is_special = blockDataCopy.is_special;
		const start_time = blockDataCopy.start_time;
		const teacher_id =blockDataCopy.teacher_id;
		const student_list = blockDataCopy.student_list;
		const locked_students = blockDataCopy.locked_students;
		const secondary_teachers = blockDataCopy.secondary_teachers.filter(st_id => st_id !== ''); // remove all empty teachers
		
		// Overwrite blockDataCopy's secondary teachers with all empty teachers removed
		blockDataCopy.secondary_teachers = secondary_teachers;

		// Save the current blocks with the new updated copied data
		let affected_blocks = [];
		affectedBlocks.forEach(affected_block => {
			const affected_block_day_id = `${affected_block.block_day_id || ''}`;

			const block_index = blocks.findIndex(block => `${block.block_day_id || ''}` === affected_block_day_id);
			if(block_index !== -1) 
			{
				blocks[block_index].course_id = course_id;
				blocks[block_index].classroom_id = classroom_id;
				blocks[block_index].end_time = end_time;
				blocks[block_index].homeroom_teacher_id = homeroom_teacher_id;
				blocks[block_index].is_lunch = is_lunch;
				blocks[block_index].is_pullout = is_pullout;
				blocks[block_index].is_special = is_special;
				blocks[block_index].start_time = start_time;
				blocks[block_index].teacher_id = teacher_id;
				blocks[block_index].student_list = student_list;
				blocks[block_index].secondary_teachers = secondary_teachers;

				// Update locked blocks for students
				student_list.forEach(student_id => {
					const student_is_locked = locked_students.includes(student_id);

					const student_index = students.findIndex(student => student.student_id === student_id);
					if(student_index !== -1)
					{
						const student_locked_block_index = students[student_index].student_locked_blocks.findIndex(locked_block_id => locked_block_id === affected_block.block_id);
						if(student_is_locked && student_locked_block_index === -1)
						{
							students[student_index].student_locked_blocks.push(affected_block.block_id);
						}
						else if(!student_is_locked && student_locked_block_index !== -1)
						{
							students[student_index].student_locked_blocks.splice(student_locked_block_index, 1);
						}
					}
				});

				affected_blocks.push(blocks[block_index]);
			}
		});

		syncBlockDayCollections(affectedBlocks, blockDataCopy);
		setBlocks([...blocks]);

		///////////////////////////////
		///// SAVE TO BACKEND //////
		////////////////////////////
		const data = {school_id:schoolInfo.school_id, schedule_version_id:schoolInfo.current_schedule_version_id, affected_blocks:affected_blocks, removed_blocks:removedBlocks, block_data:blockDataCopy};
		const apiName = process.env.REACT_APP_ELEM_ENDPOINT_NAME;
		const url = '/elementary/save-block';
		const myInit = { // OPTIONAL
			response: true,
			body: JSON.stringify(data),
		};
	
		let saveSucceeded = false;
		try {
			const result = await API.post(apiName, url, myInit);
			const response_data = result.data;

			const new_block_ids = response_data.new_block_ids;

			// Keep student lock references aligned if a brand new block received its real id.
			new_block_ids.forEach(new_block_id_combo => {
				const old_block_id = `${new_block_id_combo.old_block_id || ''}`;
				const new_block_id = `${new_block_id_combo.new_block_id || ''}`;
				if(!old_block_id || !new_block_id || old_block_id === new_block_id) return;

				students.forEach((student) => {
					const locked_block_index = student.student_locked_blocks.findIndex((locked_block_id) => `${locked_block_id}` === old_block_id);
					if(locked_block_index !== -1) student.student_locked_blocks[locked_block_index] = new_block_id;
				});
			});

			const refreshed_blocks = await getData('blocks', '/elementary/get-blocks', {school_id:schoolInfo.school_id, schedule_version_id:schoolInfo.current_schedule_version_id}, true, 'elementary');
			setBlocks(refreshed_blocks);
			setIsSaving(false);
			saveSucceeded = true;
		} catch(e)
		{
			console.log(e.response);
			const response_error = e && e.response && e.response.data && e.response.data.msg ? e.response.data.msg : 'Unable to save this block right now.';
			setSaveError(response_error);
			setIsSaving(false);
		}

		if(saveSucceeded) closeSidePanel();
	}

	const handleExitWithoutSaving = async (save_before_exiting = false) => {
		if(changesMade)
		{
			save_before_exiting = true;

			const options =  {
				title: "Would you like to save your changes?",
				dangerMode: true,
				buttons: {
					cancel: {
						text: "Exit without saving",
						value: false,
						visible: true,
						className: 'gray-btn'
					},
					confirm: {
						text: "Continue Working",
						value: true,
						visible: true,
						className: 'blue-btn'
					},
				},
				content: (
					<div>
						<p>You have changes that have not been saved. Do you want to save them now?</p>
					</div>
				)
			}
		
			save_before_exiting = await swal(options);
		}
		
		if(!save_before_exiting) 
		{
			// Remove any added blocks
			addedBlocks.forEach(added_block => {
				const added_block_day_id = added_block.block_day_id;
				const block_index = blocks.findIndex(block => `${block.block_day_id || ''}` === `${added_block_day_id || ''}`);
				if(block_index !== -1) blocks.splice(block_index, 1);
			});

			// Add any removed blocks
			removedBlocks.forEach(removed_block => {
				blocks.push(removed_block);
			});

			if(!changesMade)
			{
				setBlocks([...blocks]);
				closeSidePanel();
				return false;
			}

			// If changes were made but user is not saving, revert the current board to have previous data
			if(blockDataImmutable) 
			{
				if(blockDataImmutable.block_id)
				{
					const course_id = blockDataImmutable.course_id;
					const classroom_id = blockDataImmutable.classroom_id;
					const end_time = blockDataImmutable.end_time;
					const homeroom_teacher_id = blockDataImmutable.homeroom_teacher_id;
					const is_lunch = blockDataImmutable.is_lunch;
					const is_pullout = blockDataImmutable.is_pullout;
					const is_special = blockDataImmutable.is_special;
					const start_time = blockDataImmutable.start_time;
					const teacher_id =blockDataImmutable.teacher_id;
					const student_list =blockDataImmutable.student_list;

					// Change all affected blocks settings back to the original settings
					affectedBlocks.forEach(affected_block => {
						const affected_block_day_id = affected_block.block_day_id;
						const block_index = blocks.findIndex(block => `${block.block_day_id || ''}` === `${affected_block_day_id || ''}`);
						if(block_index !== -1) 
						{
							blocks[block_index].course_id = course_id;
							blocks[block_index].classroom_id = classroom_id;
							blocks[block_index].end_time = end_time;
							blocks[block_index].homeroom_teacher_id = homeroom_teacher_id;
							blocks[block_index].is_lunch = is_lunch;
							blocks[block_index].is_pullout = is_pullout;
							blocks[block_index].is_special = is_special;
							blocks[block_index].start_time = start_time;
							blocks[block_index].teacher_id = teacher_id;
							blocks[block_index].student_list = student_list;
						}
					});
				}
			}

			setBlocks([...blocks]);
			closeSidePanel();
		}
		else if(blockData)
		{
			swal.close();
		}
	}

	const submitCheck = (e) => {
		const key_code = e.keyCode;
		if (key_code === 13)
		{
			e.preventDefault();
			saveBlock();
		}
	}

	//////////////////////
	///// USE EFFECT /////
	//////////////////////
	useEffect(() => {
		const classroom_options = classrooms.reduce((results, classroom) => {
				const classroom_id = classroom.classroom_id;
				const classroom_name = classroom.classroom_name;

				const new_option = {'value': classroom_id, 'display': classroom_name};
				results.push(new_option);
				return results;
		}, [{'value': null, display:'--'}]);

		setClassroomOptions([...classroom_options]);
	}, [classrooms]);

	useEffect(() => {
		if(showStudentSearch) 
		{
			const suggested_students = (blockDataCopy.homeroom_teacher_id) ? students.filter(student => {
				const student_homeroom_teacher_id = student.homeroom_teacher_id;
				if(student_homeroom_teacher_id && (student_homeroom_teacher_id === blockDataCopy.homeroom_teacher_id)) return true;
				return false;
			}) : [];
			setSearchResults(s => ({...s, ...{suggested_students:suggested_students}, ...{students:students}}));
		}
	}, [showStudentSearch, students, blockDataCopy])

	useEffect(() => {
		if(!onEditorStateChange) return;
		if(!blockData || !blockDataCopy)
		{
			onEditorStateChange(null);
			return;
		}

		onEditorStateChange({
			blockData,
			blockDataCopy,
			blockDataImmutable,
			affectedBlocks,
			removedBlocks,
			addedBlocks
		});
	}, [addedBlocks, affectedBlocks, blockData, blockDataCopy, blockDataImmutable, onEditorStateChange, removedBlocks]);

	useEffect(() => {
		if(!blockDataCopy || !blockDataCopy.block_id || affectedBlocks.length === 0) return;
		if(debouncedStartTime !== blockDataCopy.start_time || debouncedEndTime !== blockDataCopy.end_time) return;

		let blocks_updated = false;
		affectedBlocks.forEach((affected_block) => {
			const affected_block_day_id = affected_block.block_day_id;
			const block_index = blocks.findIndex((block) => `${block.block_day_id || ''}` === `${affected_block_day_id || ''}`);
			if(block_index === -1) return;

			if(blocks[block_index].start_time !== debouncedStartTime)
			{
				blocks[block_index].start_time = debouncedStartTime;
				blocks_updated = true;
			}

			if(blocks[block_index].end_time !== debouncedEndTime)
			{
				blocks[block_index].end_time = debouncedEndTime;
				blocks_updated = true;
			}
		});

		if(blocks_updated) setBlocks([...blocks]);
	}, [affectedBlocks, blockDataCopy, blocks, debouncedEndTime, debouncedStartTime, setBlocks]);

	useEffect(() => {
			if(blockData) 
			{
				if(!blockDataCopied || (blockData.block_id !== blockDataCopyBlockId))
				{
				const affected_blocks = (blockData.block_id) ? blocks.filter((temp_block) => `${temp_block.block_id}` === `${blockData.block_id}`) : [];
				const added_blocks = [];

				// Set/Create a block id
				const block_id = (blockData.block_id) ? blockData.block_id : createUniqueObjectID(5);
				blockData.block_id = block_id;
				if(!blockData.block_day_id) blockData.block_day_id = createUniqueObjectID(8);

				// If this is a brand new block, add it to affectedBlocks and blocks (to display on board)
				if(affected_blocks.findIndex(affected_block => `${affected_block.block_day_id || ''}` === `${blockData.block_day_id}`) === -1) 
				{
					affected_blocks.push(blockData);
					added_blocks.push(blockData);
					blocks.push(blockData);

					setBlocks([...blocks]);
					setAddedBlocks([...added_blocks]);
				}

				syncBlockDayCollections(affected_blocks, blockData);

				// Set block name
				const course_info = (blockData.course_id) ? courses.find(course => course.course_id === blockData.course_id) : null;
				const course_name = (course_info) ? course_info.name : null;
				const course_code = (course_info) ? course_info.course_code : null;

				const block_name = (course_info) ? `${course_name} ${course_code && `(${course_code})`}` : ((blockData.is_lunch === '1') ? 'Lunch' : 'Untitled');
				blockData.block_name = block_name;

				// Set locked students
				const locked_students = blockData.student_list.reduce((results, student_id) => {
					const student_info = students.find(student => student.student_id === student_id);
					const student_locked_blocks = (student_info) ? student_info.student_locked_blocks : [];
					const student_locked = student_locked_blocks.includes(blockData.block_id);
					if(student_locked) results.push(student_id);
					return results;
				}, []);
				blockData.locked_students = locked_students;

				const copy_of_block_data = cloneObj(blockData);
				const block_data_immutable = cloneObj(blockData);

				setAffectedBlocks(affected_blocks);
				setBlockDataCopy(copy_of_block_data);
				setBlockDataImmutable(block_data_immutable);
				setBlockDataCopied(true);

				if(copy_of_block_data.is_lunch !== '1')
				{
					const homeroom_teacher_full_name = createTeacherFullName(copy_of_block_data.homeroom_teacher_id);
					const teaching_teacher_full_name = createTeacherFullName(copy_of_block_data.teacher_id);

					setHomeroomTeacherName(homeroom_teacher_full_name);
					setTeachingTeacherName(teaching_teacher_full_name);
				}
			}

			setCurrentScreen(1);
			setShowStudentSearch(false);
			setSidePanelClosing(false);
		}
	}, [blockData, blockDataCopied, blockDataCopyBlockId, addedBlocks, blocks, courses, students, createTeacherFullName, setBlocks, setSidePanelClosing, syncBlockDayCollections]);

	//console.log({blockData, blockDataCopy, affectedBlocks, removedBlocks, addedBlocks, students});
	//console.log({blocks});
	//console.log({searchResults});
	//console.log({sidePanelClosing});

	const screen_options = [{value:1, display:'Block Info'}, {value:2, display:'Students'}];
	const block_color = (!blockDataCopy) ? 'gray' : ((blockDataCopy.is_lunch === '1') ? 'pastel-yellow' : ((blockDataCopy.is_special === '1') ? 'green' :((blockDataCopy.is_pullout === '1') ? 'orange' : 'dark-purple')));

	return (
		<div className={`elem-modal-screen parent-div ${blockData ? 'open' :(sidePanelClosing ? 'close' : '')}`} ref={side_panel_ref}>
			<div id='elem-top-bar' className={block_color}>
				<div className='elem-modal-close' onClick={() => handleExitWithoutSaving(false)}>&times;</div>
				<div id='elem-top-bar-btns'>
					{(blockData && blockData.block_id && blockData.locked === '0') &&
						<div className='tooltip-left align-right' data-tooltip-text='Delete Block' style={{width:'fit-content'}} onClick={() => removeBlock(blockData.block_id)}>
							<FontAwesomeIcon icon={faTrashAlt} className='opaque-link'/>
						</div>
					}
				</div>
			</div>
			<div className='elem-modal-screen-content'>
				<div className='full-width'>
					{(!blockDataCopy || !blockData) ?
						(
							<div className='dashboard-database-message-container'>
								<img src={require('../../../images/balls.gif')} alt='loading...' style={{height:'80px'}}/>
							</div>
						):
						(
							<div>
								<div id='elem-edit-block-main-info'>
									<div className='elem-edit-block-label'>Course</div>
									<input id='elem-edit-block-name' value={blockDataCopy.block_name} onFocus={() => setSearchResults(current_results => ({...current_results, course_name:[...courses]}))} onChange={(e) => updateState(e, 'block_name', { callback:() => fuzzySearch(e, 'courses', 'course_name'), updateBoardPreview:false })} onKeyDown={submitCheck} disabled={blockData.locked === '1'}/>
									{(searchResults['course_name'].length > 0) &&
										<div className='elem-suggestion-box-parent parent-div' ref={course_search_ref}>
											<div className='elem-suggestion-box'>
												{searchResults['course_name'].map((search_result, index) => {
													const course_id = search_result.course_id;
													const course_name = search_result.name;
													const course_code = search_result.course_code;
													return (
														<div className='search-add-search-result' key={index}>
															<div>{course_code && `(${course_code}) `}{course_name}</div>
															<div className='search-add-add-btn' onClick={() => updateBlockName(course_id, `${course_name} (${course_code})`)}>Add</div>
														</div>
													)
												})}
											</div>
										</div>
									}
									<div className='elem-edit-block-label'>Block Type</div>
									<div className='elem-edit-block-type-options'>
										{allowedBlockTypeSet.has('block') && (
											<div className={`elem-edit-block-type-option ${blockDataCopy.is_special !== '1' && blockDataCopy.is_pullout !== '1' && blockDataCopy.is_lunch !== '1' ? 'elem-edit-block-type-option-selected' : ''}`} onClick={() => changeBlockType('block')}>
												Block
											</div>
										)}
										{allowedBlockTypeSet.has('special') && (
											<div className={`elem-edit-block-type-option ${blockDataCopy.is_special === '1' ? 'elem-edit-block-type-option-selected' : ''}`} onClick={() => changeBlockType('special')}>
												Special
											</div>
										)}
										{allowedBlockTypeSet.has('pullout') && (
											<div className={`elem-edit-block-type-option ${blockDataCopy.is_pullout === '1' ? 'elem-edit-block-type-option-selected' : ''}`} onClick={() => changeBlockType('pullout')}>
												Pullout
											</div>
										)}
										{allowedBlockTypeSet.has('lunch') && (
											<div className={`elem-edit-block-type-option ${blockDataCopy.is_lunch === '1' ? 'elem-edit-block-type-option-selected' : ''}`} onClick={() => changeBlockType('lunch')}>
												Lunch
											</div>
										)}
									</div>
									<div className='elem-edit-block-screen-options'>
										{screen_options.map(screen_info => {
											return (
												<div className={`${currentScreen === screen_info.value && 'elem-edit-block-screen-option-selected'} elem-edit-block-screen-option`} onClick={() => setCurrentScreen(screen_info.value)} key={screen_info.value}>{screen_info.display}</div>
											)
										})}
									</div>
									<div className={currentScreen !== 1 ? 'hide' : 'elem-edit-block-screen'}>
										<div id='elem-edit-block-time-container'>
											<input id='elem-edit-block-start-time' type='time' min='07:00' max='16:00' value={blockDataCopy.start_time} onChange={(e) => updateState(e, 'start_time', { updateBoardPreview:false })} onKeyDown={submitCheck} disabled={blockData.locked === '1'}/>
											<div>to</div>
											<input id='elem-edit-block-end-time' type='time' min='07:00' max='16:00' value={blockDataCopy.end_time} onChange={(e) => updateState(e, 'end_time', { updateBoardPreview:false })} onKeyDown={submitCheck} disabled={blockData.locked === '1'}/>
										</div>
										<div id='elem-edit-block-days-container'>
											{days.map((day, index) => {
												const day_included_in_affected_blocks = affectedBlocks.reduce((results, affected_block) => {
													if(`${affected_block.day_number}` === `${day}`) results = true;
													return results;
												}, false);

												return (
													<div key={index}>
														<div>Day {day}</div>
														{(day_included_in_affected_blocks) ? 
															(
																<FontAwesomeIcon className='fas-checkbox-checked' icon={faCheckSquare} onClick={blockData.locked === '0' ? () => updateDays('remove', day) : null}/>
															) : 
															(
																<FontAwesomeIcon className='fas-checkbox-unchecked' icon={faSquare} onClick={blockData.locked === '0' ? () => updateDays('add', day) : null}/>
															)
														}
													</div>
												)
											})}
										</div>
										{timeErrorMessage && <div className='error-message-no-box'>{timeErrorMessage}</div>}
											{(blockDataCopy.is_pullout === '1') ?
											(
												<div id='elem-edit-block-pull-out-teacher-container'>
													<div className='elem-edit-block-label'>Pull Out Teacher</div>
													<input id='elem-edit-block-pullout-teacher' value={teachingTeacherName} onFocus={() => setSearchResults(current_results => ({...current_results, teaching_teacher:[...teachers]}))} onChange={(e) => handleExtraTeacherSearch(e, 'teaching_teacher')} disabled={blockData.locked === '1'}/>
													{teachingTeacherError && <div className='error-message-no-box'>{teachingTeacherError}</div>}
													{(searchResults['teaching_teacher'].length > 0) &&
														<div className='elem-suggestion-box-parent parent-div' ref={teaching_teachers_ref}>
															<div className='elem-suggestion-box'>
																{searchResults['teaching_teacher'].map((search_result, index) => {
																	const search_teacher_id = search_result.teacher_id;
																	const search_teacher_first_name = search_result.first_name;
																	const search_teacher_last_name = search_result.name;
																	const search_teacher_full_name =  `${search_teacher_last_name}, ${search_teacher_first_name}`;

																	return (
																		<div className='search-add-search-result' key={index}>
																			<div>{search_teacher_full_name}</div>
																			<div className='search-add-add-btn' onClick={() => updatePullOutTeacher(search_teacher_id)}>Add</div>
																		</div>
																	)
																})}
															</div>
														</div>
													}
													{blockDataCopy.secondary_teachers.map((secondary_teacher_id, i) => {
														return (
															<EditBlockSecondaryTeacher teachers={teachers} blockData={blockData} secondaryTeacherID={secondary_teacher_id} changeSecondaryTeacher={changeSecondaryTeacher} removeSecondaryTeacher={removeSecondaryTeacher} key={i}/>
														)
													})}
													{blockDataCopy.teacher_id &&
														<div className='blue-link align-right small-text edit-section-secondary-teacher-blue-link' onClick={() => addSecondaryTeacher({secondary_teacher_id:''})}>Add Another Pullout Teacher</div>
													}
												</div>
											) : (blockDataCopy.is_special === '1') ?
											(
												<div id='elem-edit-block-special-teacher-container'>
													<div className='elem-edit-block-label'>Special Teacher</div>
													<input id='elem-edit-block-specials-teacher' value={teachingTeacherName} onFocus={() => setSearchResults(current_results => ({...current_results, teaching_teacher:[...teachers]}))} onChange={(e) => handleExtraTeacherSearch(e, 'teaching_teacher')} disabled={blockData.locked === '1'}/>
													{teachingTeacherError && <div className='error-message-no-box'>{teachingTeacherError}</div>}
													{(searchResults['teaching_teacher'].length > 0) &&
														<div className='elem-suggestion-box-parent parent-div' ref={teaching_teachers_ref}>
															<div className='elem-suggestion-box'>
																{searchResults['teaching_teacher'].map((search_result, index) => {
																	const search_teacher_id = search_result.teacher_id;
																	const search_teacher_first_name = search_result.first_name;
																	const search_teacher_last_name = search_result.name;
																	const search_teacher_full_name =  `${search_teacher_last_name}, ${search_teacher_first_name}`;

																	return (
																		<div className='search-add-search-result' key={index}>
																			<div>{search_teacher_full_name}</div>
																			<div className='search-add-add-btn' onClick={() => updateExtraTeacher(search_teacher_id, 'teaching_teacher')}>Add</div>
																		</div>
																	)
																})}
															</div>
														</div>
													}
													{blockDataCopy.secondary_teachers.map((secondary_teacher_id, i) => {
														return (
															<EditBlockSecondaryTeacher teachers={teachers} blockData={blockData} secondaryTeacherID={secondary_teacher_id} changeSecondaryTeacher={changeSecondaryTeacher} removeSecondaryTeacher={removeSecondaryTeacher} key={i}/>
														)
													})}
													{blockDataCopy.teacher_id &&
														<div className='blue-link align-right small-text edit-section-secondary-teacher-blue-link' onClick={() => addSecondaryTeacher({secondary_teacher_id:''})}>Add Another Specials Teacher</div>
													}
													<div className='elem-edit-block-label'>Homeroom Teacher</div>
													<input id='elem-edit-block-homeroom-teacher' value={homeroomTeacherName} onFocus={() => setSearchResults(current_results => ({...current_results, homeroom_teacher:[...teachers]}))} onChange={(e) => handleExtraTeacherSearch(e, 'homeroom_teacher')} disabled={blockData.locked === '1'}/>
													{homeroomTeacherError && <div className='error-message-no-box'>{homeroomTeacherError}</div>}
													{(searchResults['homeroom_teacher'].length > 0) &&
														<div className='elem-suggestion-box-parent parent-div' ref={homeroom_teachers_ref}>
															<div className='elem-suggestion-box'>
																{searchResults['homeroom_teacher'].map((search_result, index) => {
																	const search_teacher_id = search_result.teacher_id;
																	const search_teacher_first_name = search_result.first_name;
																	const search_teacher_last_name = search_result.name;
																	const search_teacher_full_name =  `${search_teacher_last_name}, ${search_teacher_first_name}`;

																	return (
																		<div className='search-add-search-result' key={index}>
																			<div>{search_teacher_full_name}</div>
																			<div className='search-add-add-btn' onClick={() => updateExtraTeacher(search_teacher_id, 'homeroom_teacher')}>Add</div>
																		</div>
																	)
																})}
															</div>
														</div>
													}
												</div>
											) : (blockDataCopy.is_lunch !== '1') ? 
											(
												<div id='elem-edit-block-pull-out-teacher-container'>
													<div className='elem-edit-block-label'>Teacher</div>
													<div id='elem-edit-block-teacher'>{teachingTeacherName}</div>
													{blockDataCopy.secondary_teachers.map((secondary_teacher_id, i) => {
														return (
															<EditBlockSecondaryTeacher teachers={teachers} blockData={blockData} secondaryTeacherID={secondary_teacher_id} changeSecondaryTeacher={changeSecondaryTeacher} removeSecondaryTeacher={removeSecondaryTeacher} key={i}/>
														)
													})}
													{blockDataCopy.teacher_id &&
														<div className='blue-link align-right small-text edit-section-secondary-teacher-blue-link' onClick={() => addSecondaryTeacher({secondary_teacher_id:''})}>Add Another Teacher</div>
													}
												</div>
											): null}
										{showClassroomField && (
											<>
												<div className='elem-edit-block-label'>Classroom</div>
												<Dropdown data={classroomOptions} currentValue={blockDataCopy.classroom_id} handleChange={(new_value) => updateState(new_value, 'classroom_id')} disabled={blockData.locked === '1'}/>
											</>
										)}
										</div>
										<div className={currentScreen !== 2 ? 'hide' : 'elem-edit-block-screen'}>
											{(blockData.locked === '0' && blockDataCopy.student_list.length > 0) &&
												<div className='select-all-row parent-div'>
													<div className={`select-all-container parent-div ${(selectedStudents.length > 0) && 'blue-text'}`} onClick={toggleSelectAll}>
														<FontAwesomeIcon icon={(selectedStudents.length > 0) ? faMinusSquare : farSquare}/>
														<FontAwesomeIcon className='select-all-arrow' icon={faSortDown}/>
													</div>
													{(selectedStudents.length > 0) &&
														<div className='select-all-actions-container parent-div'>
															<div className='tooltip' data-tooltip-text='Lock students into section' onClick={() => toggleLockStudents('lock')}>
																<FontAwesomeIcon className='select-all-action-btn tooltip' icon={faLock}/>
															</div>
															<div className='tooltip' data-tooltip-text='Remove students' onClick={bulkRemoveStudents}>
																<FontAwesomeIcon className='select-all-action-btn tooltip' icon={faTrashAlt}/>
															</div>
														</div>
													}
												</div>
											}
											<div className='elem-edit-block-student-list'>
												{blockDataCopy.student_list.map(student_id => {
													const student_info = students.find(student => student.student_id === student_id);
													const student_first_name = (student_info) ? student_info.first_name : null;
													const student_last_name = (student_info) ? student_info.last_name : null;
													const student_grade = (student_info) ? student_info.grade : null;
													const student_code = (student_info) ? student_info.student_code : null;
													const student_locked = (blockDataCopy.locked_students.includes(student_id) || blockData.locked === '1');
													const opposite_locked_type = student_locked ? 'unlock' : 'lock';
													const student_selected = selectedStudents.includes(student_id);

													const student_is_pulled_out = blocks.reduce((results, other_block) => {
														const other_block_id = other_block.block_id;
														const other_block_is_pullout = other_block.is_pullout === '1';
														const other_block_student_list = other_block.student_list;

														// If same block, skip it
														if(blockData.block_id === other_block_id) return results;
														// If student not in other block, skip it
														if(!other_block_student_list.includes(student_id)) return results;

														// Check if the two blocks overlap
														const block_data_time = {start_time:blockDataCopy.start_time, end_time:blockDataCopy.end_time};
														const other_block_time = {start_time:other_block.start_time, end_time:other_block.end_time};
														
														// If other block is pull out and the blocks overlap, show that student is pulled out
														if(other_block_is_pullout && overlapCheck(block_data_time, other_block_time)) results = true;
														return results;
													}, false);

													return (
														<div className='elem-edit-block-student parent-div' key={student_id}>
															<FontAwesomeIcon className={student_locked ? 'light-gray-text dropdown_disabled' : (student_selected ? 'blue-text' : 'dark-gray-text')} icon={student_locked ? faSquare : (student_selected ? faCheckSquare : farSquare)} onClick={student_locked ? ()=>{} : () => toggleSelectStudent(student_id)}/>
															<div>
																<div className='elem-edit-block-student-name'>{student_last_name}, {student_first_name} {student_grade && student_grade} {student_code && `(${student_code})`} {student_is_pulled_out && '(Pulled Out)'}</div>
																<FontAwesomeIcon icon={faLock} className={`edit-section-lock-student-icon cursor-pointer ${student_locked ? 'bright-yellow-text' : 'gray-text'}`} onClick={() => toggleLockStudents(opposite_locked_type, student_id)}/>
															</div>
															{(!student_locked) &&
																<FontAwesomeIcon className='gray-to-red-link' icon={faTimes} onClick={() => removeStudent(student_id)}/>
															}
														</div>
													)
												})}
											</div>
											{(blockData.locked === '0') &&
												<div className='edit-section-add-new-btn blue-link' onClick={() => setShowStudentSearch(!showStudentSearch)}>
													<FontAwesomeIcon icon={faPlusCircle}/>
													<div>Add Student</div>
												</div>
											}
											{showStudentSearch && 
												<div className='search-add-row-container'>
													<div className='search-add-search-container'>
														<div className='search-add-adding-input-container'>
															<input className='search-add-adding-input' onChange={(e) => fuzzySearch(e, 'students')} placeholder='Search for students' autoFocus={true}/>
															<FontAwesomeIcon className='search-add-adding-input-icon' icon={faSearch}/>
														</div>
														<div className='cancel-student-search parent-div' onClick={() => setShowStudentSearch(false)}><FontAwesomeIcon className='x-cancel search-add-cancel-new-btn' icon={faTimes}/></div>
														
													</div>
													{searchResults.students.length !== 0 &&
														<>
														<div className='search-add-search-results' style={{marginTop:'20px', padding:'0px'}}>
															{(searchResults.suggested_students.length > 0) &&
																<div className='search-add-did-you-mean'>Students in this Homeroom:</div>
															}
															{searchResults.suggested_students.map((search_student, i) => {
																const student_in_section_index = blockDataCopy.student_list.findIndex(student_id => student_id === search_student.student_id);
																const student_in_section = (student_in_section_index !== -1) ? true : false;
																const student_locked = blockDataCopy.locked_students.includes(search_student.student_id);

																return (
																	<div className='search-add-search-result' key={i} style={{gridTemplateColumns:'1fr auto', textAlign:'left'}}>
																		<div>{search_student.last_name}, {search_student.first_name}</div>
																		<div>
																			{(student_locked) ? 
																				(
																					<span className='bright-yellow-text' >Locked <FontAwesomeIcon icon={faLock} style={{margin:'0px 5px'}}/></span>
																				):(student_in_section) ? 
																				(
																					<div className='search-add-already-selected'>Selected</div>
																				): 
																				(
																					<div className='search-add-add-btn student-search-btn' onClick={() => addStudent(search_student.student_id)}>Add</div>
																				)
																			}
																		</div>
																	</div>
																)
															})}
															{(searchResults.students.length > 0) &&
																<div className='search-add-did-you-mean'>Other Students:</div>
															}
															{searchResults.students.map((search_student, i) => {
																const search_student_first_name = search_student.first_name;
																const search_student_last_name = search_student.last_name;
																const search_student_grade = search_student.grade;
																const search_student_code = search_student.student_code;
																const student_in_section_index = blockDataCopy.student_list.findIndex(student_id => student_id === search_student.student_id);
																const student_in_section = (student_in_section_index !== -1) ? true : false;
																const student_locked = blockDataCopy.locked_students.includes(search_student.student_id);

																return (
																	<div className='search-add-search-result' key={i} style={{gridTemplateColumns:'1fr auto', textAlign:'left'}}>
																		<div>{search_student_last_name}, {search_student_first_name} {search_student_grade && search_student_grade} {search_student_code && `(${search_student_code})`}</div>
																		<div>
																			{(student_locked) ? 
																				(
																					<span className='bright-yellow-text' >Locked <FontAwesomeIcon icon={faLock} style={{margin:'0px 5px'}}/></span>
																				):(student_in_section) ? 
																				(
																					<div className='search-add-already-selected'>Selected</div>
																				): 
																				(
																					<div className='search-add-add-btn student-search-btn' onClick={() => addStudent(search_student.student_id)}>Add</div>
																				)
																			}
																		</div>
																	</div>
																)
															})}
														</div>
														</>
													}
												</div>
											}
										</div>
								</div>
								{renderBlockTypeExtras && renderBlockTypeExtras({
									blockData,
									blockDataCopy,
									blockDataImmutable,
									isLocked: blockData.locked === '1',
									markChanged: () => setChangesMade(true)
								})}
							</div>
						)
					}
				</div>
			</div>
			<div id='elem-edit-block-save-row'>
				{(blockData && blockDataCopy) &&
					<>
					{(blockData.locked === '1') ? 
						(
							<div><FontAwesomeIcon className='bright-yellow-text' icon={faLock} style={{marginRight:'10px'}}/> This block is current locked</div>
						):
						(
							<>
							{saveError && <div className='error-message-no-box' style={{marginBottom:'10px'}}>{saveError}</div>}
							{isSaving ? 
							(
								<div className={`btn btn-large ${block_color}-btn`}><img src={loading_gif} alt='loading...' style={{height:'20px'}}/></div>
							):
							(
								<div className={`btn btn-large ${block_color}-btn`} onClick={saveBlock}>{saveButtonLabel}</div>
							)}
							
							</>
						)
					}
					</>
				}
			</div>
		</div>
	);
}
